import type { QueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import { fetchAuthSession } from "aws-amplify/auth";
import axios from "axios";

import { resetAuthRedirectCount } from "../../components/Auth/auth-redirect";
import { sameToken } from "../../components/Auth/jwt-token";
import { getSessionFromAuthId } from "../../components/Auth/use-session-from-authid";
import type { AxiosInstances } from "../../features/axios-instances";
import { axiosGet, axiosPost } from "../fetch/query";

import { AuthActionEnum } from "./auth-context";
import { signOutHandler } from "./auth-mutations";
import { markAuthRefresh, markSessionStarted } from "./auth-telemetry";
import {
  setCognitoStorage,
  synchPhpSessionWithCognitoStorage,
} from "./cognito-storage";
import { NoSessionFoundError } from "./no-session-found-error";

export { NoSessionFoundError };

const PAT_STORAGE_KEY = "ms-pat-authorization";

// Returns the current opaque authorization token (`jwt-as:` or `pat:`), if any.
//
// `jwt-as:` lives on `window._msAuth.session.authorization`, injected by PHP
// for masquerading sessions. `pat:` lives in sessionStorage as a single-use
// hand-off from /auth to /dashboard (PHP doesn't echo the PAT back; it mints
// its own Cognito JWT). The PAT is cleared the first time `fetchSession`
// consumes it so that a later PHP-side logout doesn't auto-re-authenticate
// the user via the same stale PAT.
export function getOpaqueAuthorization(): string | null {
  if (window._msAuth?.session?.authorization?.startsWith("jwt-as:")) {
    return window._msAuth.session.authorization;
  }
  try {
    const pat = window.sessionStorage.getItem(PAT_STORAGE_KEY);
    if (pat?.startsWith("pat:")) {
      return pat;
    }
  } catch {
    // sessionStorage can throw in private/blocked contexts — treat as absent.
  }
  return null;
}

export function setPatAuthorization(pat: string | null): void {
  try {
    if (pat) {
      window.sessionStorage.setItem(PAT_STORAGE_KEY, pat);
    } else {
      window.sessionStorage.removeItem(PAT_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

type SessionParams = {
  queryClient: QueryClient;
  axiosInstances: AxiosInstances;
  userPoolsWebClientId: string;
  trustDevice: boolean;
  // Consumer apps (everything but the auth app) only *check* their session
  // against Platform through `validate-auth`; SessionProvider hands off to the
  // auth app when there is none. The auth app keeps minting the PHP session
  // through `login-auth`.
  validateSession?: boolean;
};

async function fetchSession({
  queryClient,
  axiosInstances,
  userPoolsWebClientId,
  trustDevice,
  validateSession,
}: SessionParams) {
  // If we are signing out, we don't want to re-sync a session that will
  // soon be revoked.
  const action = new URLSearchParams(window.location.search).get("action");
  if (action === AuthActionEnum.SignOut) {
    await signOutHandler();
    return null;
  }

  const authorization = getOpaqueAuthorization();
  if (authorization) {
    axiosInstances.authorize(`Bearer ${authorization}`);
  }

  if (authorization?.startsWith("pat:")) {
    try {
      // Re-establish the PHP session cookie from the stored PAT so subsequent
      // page navigations (Main.ts -> /dashboard) are authenticated. Also the
      // Amplify bridge tokenProvider relies on this same PAT being readable
      // from sessionStorage to mint a Cognito JWT for AppSync — keep it
      // around, clear it on failure or explicit sign-out (see signOutHandler
      // and AuthProvider.postSignout).
      await axiosPost({
        entity: "auth",
        verb: "login",
        token: `Bearer ${authorization}`,
      });
    } catch {
      setPatAuthorization(null);
      axiosInstances.authorize("");
      return null;
    }
    return null;
  }

  if (authorization?.startsWith("jwt-as:")) {
    return null;
  }

  // We keep the access token that is known from Platform, if any
  // This will allow us to later check if the token is still fresh on
  // Platform, and prevent unnecessary calls to login-auth.
  const authSessionAccessToken = top?._msAuth?.session?.accessToken;
  await synchPhpSessionWithCognitoStorage(userPoolsWebClientId);
  try {
    let tokens: Awaited<ReturnType<typeof getSessionFromAuthId>> = null;
    try {
      tokens = await getSessionFromAuthId();
    } catch (authIdError) {
      global.console.error("Failed to authenticate via authId", authIdError);
    }
    if (tokens !== null) {
      axiosInstances.authorize(`Bearer ${tokens.cognito_authorization}`);
      // Validated before being stored: tokens Platform refuses must not
      // replace the Cognito session this origin already holds.
      if (validateSession && !(await isValidOnPlatform())) {
        axiosInstances.authorize("");
        return null;
      }
      await setCognitoStorage(
        userPoolsWebClientId,
        tokens.cognito_access_token,
        tokens.cognito_refresh_token,
        tokens.cognito_id_token,
      );
      markAuthRefresh("auth-id");
      markSessionStarted();
      return {
        tokens: {
          accessToken: tokens.cognito_access_token,
          refreshToken: tokens.cognito_refresh_token,
        },
      };
    }

    const session = await fetchAuthSession({ forceRefresh: true });

    if (!session || !session.tokens?.accessToken) {
      // The forced refresh produced no tokens: the Cognito refresh token is
      // expired (or absent). Resolving to null is the whole verdict: the
      // provider owns what comes next (SessionProvider redirects to the auth
      // app, the auth app shows its login).
      throw new NoSessionFoundError();
    }

    if (session.tokens.accessToken.payload.exp) {
      const expiresIn =
        session.tokens.accessToken.payload.exp * 1000 - Date.now();
      setTimeout(() => {
        markAuthRefresh("timer");
        invalidateSession(queryClient);
      }, expiresIn);
    }

    const accessToken = session.tokens.accessToken
      .toString()
      .replace(/^jwt:/, "");

    axiosInstances.authorize(`Bearer jwt:${accessToken}`);

    if (validateSession) {
      if (!(await isValidOnPlatform())) {
        axiosInstances.authorize("");
        return null;
      }
      markSessionStarted();
      return session;
    }

    if (!(
      authSessionAccessToken &&
      sameToken(authSessionAccessToken, accessToken) &&
      top?._msApiEndpoint === top?.location.origin
    )) {
      // We are on the same domain as the API, and the API already knows
      // our credentials. No need to login back, this saves a roundtrip.
      try {
        const response = await axiosPost<{
          connectUser: boolean;
          trust_2fa: string;
        }>({
          entity: "auth",
          verb: "login",
          token: `Bearer jwt:${accessToken}`,
          body: trustDevice ? { trust_device: true } : undefined,
        });

        if (
          response.data &&
          response.data.connectUser &&
          response.data.trust_2fa
        ) {
          localStorage.setItem("trust_2fa", response.data.trust_2fa);
        }
      } catch (loginError) {
        if (!isTransientLoginFailure(loginError)) {
          throw loginError;
        }
        // A gateway timeout or network blip on login-auth says nothing about
        // the Cognito session — the access token above is valid and already
        // authorized on every instance. Letting it reach the outer catch
        // clears the bearer, and in a hidden tab that catch does not sign
        // out either, so the page keeps running with no credentials and no
        // way back. Observed in production: a 504 here at 16:39 left a tab
        // broken until the user touched it the next morning.
        //
        // Continue on the valid session instead. If the platform session
        // really is missing, catch-401 re-runs fetchSession, which retries
        // this call.
        global.console.warn(
          "[react-shared]: login-auth failed transiently, keeping the Cognito session",
          loginError,
        );
      }
    }
    markSessionStarted();
    return session;
  } catch (error) {
    axiosInstances.authorize("");
    if (error instanceof NoSessionFoundError) {
      return null;
    }
    global.console.error(
      "Could not use credentials from session, signing out",
      error,
    );
    if (document.hidden === false) {
      // Only explicitly sign out if the user is not in a hidden tab. We
      // don't want to mess with a user trying to log in in another tab.
      await signOutHandler();
    }
    throw error;
  }
}

// Asks Platform whether it still recognizes the bearer just authorized. False
// only on a 401.
//
// Same endpoint as php-client's `validateAuth()`, sent through the platform
// instance instead: commitdiff and raiponce do not register the php instance,
// and the generated call would then hit the app's own origin without a bearer.
async function isValidOnPlatform(): Promise<boolean> {
  try {
    await axiosGet({ entity: "auth", verb: "validate" });
    resetAuthRedirectCount();
    return true;
  } catch (error) {
    // Once mounted, catch-401 turns a repeated validate 401 into a
    // NoSessionFoundError: same verdict as the raw 401.
    if (
      error instanceof NoSessionFoundError ||
      (axios.isAxiosError(error) && error.response?.status === 401)
    ) {
      return false;
    }
    // 5xx, network: the tokens are fresh, so fail open rather than sign every
    // app out on a Platform outage. Later API 401s still reach catch-401.
    global.console.error(
      "[react-shared]: validate-auth failed, keeping the Cognito session",
      error,
    );
    return true;
  }
}

// Only a verdict about the credential itself is worth tearing the session
// down for. Everything else — no response at all, a gateway 5xx, 408, 429 —
// is the platform being briefly unreachable, which a later call can clear.
function isTransientLoginFailure(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  const status = error.response?.status;
  if (status === undefined) {
    // Network failure, timeout, aborted preflight: never reached a verdict.
    return true;
  }
  return status >= 500 || status === 408 || status === 429;
}

const cognitoAuthSessionKey = "cognito.auth.session";

export function invalidateSession(queryClient: QueryClient): void {
  queryClient.invalidateQueries({
    queryKey: [cognitoAuthSessionKey],
  });
}

// Re-runs the full session bootstrap (Cognito refresh + platform login-auth)
// and returns the resulting bearer, or null when no session could be
// established. Goes through the session query so it dedups with any refetch
// already in flight (expiry timer, catch-401 invalidation) and keeps
// SessionProvider's data warm — no loader flash.
export async function refreshSessionBearer({
  queryClient,
  axiosInstances,
  userPoolsWebClientId,
  trustDevice,
  validateSession,
}: SessionParams): Promise<string | null> {
  markAuthRefresh("gate");
  const session = await queryClient.fetchQuery({
    queryKey: [cognitoAuthSessionKey, userPoolsWebClientId],
    queryFn: () =>
      fetchSession({
        queryClient,
        axiosInstances,
        userPoolsWebClientId,
        trustDevice,
        validateSession,
      }),
    staleTime: 0,
  });
  const accessToken = session?.tokens?.accessToken;
  if (!accessToken) {
    return null;
  }
  return `Bearer jwt:${accessToken.toString().replace(/^jwt:/, "")}`;
}

export type SessionRecovery = "recovered" | "lost" | "unavailable";

// A session recovered this recently that still ends in an auth failure is not
// an expired session: the credential is fresh and something keeps refusing it.
const SESSION_RECOVERY_COOLDOWN_MS = 30_000;
let lastRecoveredAt = 0;
let recoveryInFlight: Promise<SessionRecovery> | null = null;

// Re-runs the session bootstrap after a page failed on auth, and reports the
// outcome:
// - "recovered": a fresh Cognito session, the page can be retried.
// - "lost": no session. SessionProvider is redirecting to the auth app, or
//   the auth app is swapping to its login: nothing left to do.
// - "unavailable": nothing to recover (opaque flow, failed bootstrap, or a
//   session recovered moments ago that did not fix the failure).
// Every crashed route on the page shares one run.
export function recoverSession(
  queryClient: QueryClient,
): Promise<SessionRecovery> {
  if (!recoveryInFlight) {
    recoveryInFlight = (async () => {
      try {
        return await runSessionRecovery(queryClient);
      } finally {
        recoveryInFlight = null;
      }
    })();
  }
  return recoveryInFlight;
}

export function _resetSessionRecovery(): void {
  lastRecoveredAt = 0;
  recoveryInFlight = null;
}

async function runSessionRecovery(
  queryClient: QueryClient,
): Promise<SessionRecovery> {
  // On an opaque flow the session query always resolves to null while the
  // provider keeps the app mounted on the opaque token: waiting for a Cognito
  // session would never end.
  if (getOpaqueAuthorization()) {
    return "unavailable";
  }
  if (Date.now() - lastRecoveredAt < SESSION_RECOVERY_COOLDOWN_MS) {
    return "unavailable";
  }
  // catch-401 has usually invalidated the session already: join that refetch
  // instead of cancelling and restarting it.
  await queryClient.refetchQueries(
    { queryKey: [cognitoAuthSessionKey] },
    { cancelRefetch: false },
  );
  const [query] = queryClient
    .getQueryCache()
    .findAll({ queryKey: [cognitoAuthSessionKey] });
  // A failed refetch keeps the previous, dead session as data: only the status
  // tells a fresh session from a failed bootstrap.
  if (query?.state.status !== "success") {
    return "unavailable";
  }
  if (!hasAccessToken(query.state.data)) {
    return "lost";
  }
  lastRecoveredAt = Date.now();
  return "recovered";
}

// Whether the last session bootstrap ended with a usable session, read from
// the cache without subscribing: for route guards, which run outside React.
export function hasSession(queryClient: QueryClient): boolean {
  if (getOpaqueAuthorization()) {
    return true;
  }
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: [cognitoAuthSessionKey] })
    .some((query) => hasAccessToken(query.state.data));
}

function hasAccessToken(session: unknown): boolean {
  return (
    typeof session === "object" &&
    session !== null &&
    "tokens" in session &&
    typeof session.tokens === "object" &&
    session.tokens !== null &&
    "accessToken" in session.tokens &&
    Boolean(session.tokens.accessToken)
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function prefetchSession({
  queryClient,
  axiosInstances,
  userPoolsWebClientId,
  trustDevice,
  validateSession,
}: SessionParams) {
  return queryClient.prefetchQuery({
    queryKey: [cognitoAuthSessionKey, userPoolsWebClientId],
    queryFn: () =>
      fetchSession({
        queryClient,
        axiosInstances,
        userPoolsWebClientId,
        trustDevice,
        validateSession,
      }),
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useSession({
  queryClient,
  axiosInstances,
  userPoolsWebClientId,
  trustDevice,
  validateSession,
}: SessionParams) {
  return useQuery({
    queryKey: [cognitoAuthSessionKey, userPoolsWebClientId],
    queryFn: () =>
      fetchSession({
        queryClient,
        axiosInstances,
        userPoolsWebClientId,
        trustDevice,
        validateSession,
      }),
  });
}
